import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, MapPin, Star, DollarSign, ExternalLink, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { updatePageMetadata, pageMetadata } from '../utils/pageMetadata';
import { useSavedPlaces } from '../contexts/SavedPlacesContext';
import { getImageVariants } from '../utils/imageUtils';
import { PlaceItem } from '../types/explore';
import PlaceDetailPanel from './PlaceDetailPanel';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { exploreService } from '../services/exploreService';
import RegistrationPrompt from './RegistrationPrompt';

const SavedItems: React.FC = () => {
  const { savedPlaces, loading, error, unsavePlace, loadSavedPlaces } = useSavedPlaces();
  const { authState } = useAuth();
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.saved.title, pageMetadata.saved.description);
  }, []);
  const { tier } = usePermissions();
  const user = authState.user;
  const [selectedPlace, setSelectedPlace] = useState<PlaceItem | null>(null);
  const [displayedPlaces, setDisplayedPlaces] = useState<PlaceItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allPlacesLoaded, setAllPlacesLoaded] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemsPerPage = 50; // Load 50 items at a time (increased for better UX)
  
  // Refs for scroll functionality
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const placeRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Touch state for mobile interactions
  const [touchedPlaceId, setTouchedPlaceId] = useState<string | null>(null);

  const getImageSrc = (photo: string, size: 'thumb' | 'medium' | 'large' = 'thumb'): string => {
    const variants = getImageVariants(photo);
    return variants[size] || variants.original || 'https://via.placeholder.com/400?text=No+Image';
  };


  // Handle place touch (mobile)
  const handlePlaceTouch = useCallback((place: PlaceItem) => {
    if (touchedPlaceId === place.id) {
      // Second touch - open details
      setSelectedPlace(place);
      setTouchedPlaceId(null);
    } else {
      // First touch - show overlay
      setTouchedPlaceId(place.id);
    }
  }, [touchedPlaceId]);

  const handleUnsave = async (placeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent overlay from triggering
    try {
      await unsavePlace(placeId);
    } catch (error) {
      // Error unsaving place
    }
  };

  const handlePlaceClick = (place: PlaceItem) => {
    setSelectedPlace(place);
  };

  const handleCloseDetail = () => {
    setSelectedPlace(null);
  };

  // Load full details from explore collection
  const handleLoadFullDetails = async (placeId: string) => {
    if (!placeId) {
      return;
    }

    setIsLoadingDetails(true);
    try {
      const fullPlace = await exploreService.getPlaceById(placeId);
      
      // Merge full place data with selected place (preserve savedAt and other saved-specific fields)
      if (selectedPlace) {
        const mergedPlace = {
          ...fullPlace,
          savedAt: selectedPlace.savedAt, // Preserve saved timestamp
        };
        setSelectedPlace(mergedPlace as PlaceItem);
      }
    } catch (error) {
      // Could add a toast notification here
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Load more places for infinite scroll
  const loadMorePlaces = useCallback(() => {
    if (isLoadingMore || displayedPlaces.length >= savedPlaces.length || allPlacesLoaded) {
      return;
    }
    
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    // Deduplicate savedPlaces by ID to prevent duplicate keys
    const uniquePlaces = savedPlaces.reduce((acc, place) => {
      if (!acc.find(p => p.id === place.id)) {
        acc.push(place);
      }
      return acc;
    }, [] as PlaceItem[]);
    
    const newPlaces = uniquePlaces.slice(startIndex, endIndex);
    
    // Filter out places that are already displayed to prevent duplicates
    const existingPlaceIds = new Set(displayedPlaces.map(p => p.id));
    const filteredNewPlaces = newPlaces.filter(place => !existingPlaceIds.has(place.id));
    
    setDisplayedPlaces(prev => [...prev, ...filteredNewPlaces]);
    setCurrentPage(nextPage);
    setIsLoadingMore(false);
  }, [currentPage, displayedPlaces.length, savedPlaces, isLoadingMore, itemsPerPage, allPlacesLoaded, displayedPlaces]);

  // Intersection Observer for infinite scroll
  const lastItemElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoadingMore || allPlacesLoaded) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && displayedPlaces.length < savedPlaces.length && !allPlacesLoaded) {
        loadMorePlaces();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, displayedPlaces.length, savedPlaces.length, loadMorePlaces, allPlacesLoaded]);

  // Initialize displayed places when savedPlaces changes
  useEffect(() => {
    if (savedPlaces.length > 0) {
      // Deduplicate savedPlaces by ID to prevent duplicate keys
      const uniquePlaces = savedPlaces.reduce((acc, place) => {
        if (!acc.find(p => p.id === place.id)) {
          acc.push(place);
        }
        return acc;
      }, [] as PlaceItem[]);
      
      const initialPlaces = uniquePlaces.slice(0, itemsPerPage);
      
      setDisplayedPlaces(initialPlaces);
      setCurrentPage(1);
      setAllPlacesLoaded(false);
    } else {
      setDisplayedPlaces([]);
      setCurrentPage(1);
      setAllPlacesLoaded(false);
    }
  }, [savedPlaces, itemsPerPage]);

  const getPriceInfo = (place: PlaceItem) => {
    if (place.price_per_night_dec) {
      return { label: 'Per Night', value: `$${place.price_per_night_dec}` };
    } else if (place.cost_per_person_min) {
      const max = place.cost_per_person_max ? `-$${place.cost_per_person_max}` : '+';
      return { label: 'Per Person', value: `$${place.cost_per_person_min}${max}` };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your saved places...</p>
        </div>
      </div>
    );
  }

  // Show registration prompt for Tier 1 users (whether error or empty)
  if (tier === 'unregistered' && (error || savedPlaces.length === 0)) {
    return (
      <>
        <div className="h-full flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">❤️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Save Your Favorites</h1>
            <p className="text-gray-600 mb-6">
              Sign in to save places, create trips, and get personalized recommendations for your Sri Lankan adventure!
            </p>
            <button
              onClick={() => setShowRegistrationPrompt(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <User size={20} className="mr-2" />
              Sign In to Save Places
            </button>
            <p className="text-sm text-gray-600 mt-4">
              <Link to="/app/chat" className="text-blue-600 hover:text-blue-700 font-medium">Chat</Link>
              {' '}with your travel assistant and{' '}
              <Link to="/app/explore" className="text-blue-600 hover:text-blue-700 font-medium">explore</Link>
              {' '}amazing places in Sri Lanka
            </p>
          </div>
        </div>
        <RegistrationPrompt
          isOpen={showRegistrationPrompt}
          onClose={() => setShowRegistrationPrompt(false)}
          variant="google_auth"
          trigger="save"
        />
      </>
    );
  }

  // Show error for authenticated users
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Show empty state for authenticated users
  if (savedPlaces.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">💔</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No saved places yet</h3>
          <p className="text-gray-600">Start exploring and save places you love by tapping the heart icon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* Desktop Layout - Only show on medium screens and up */}
      <div className="hidden md:flex flex-col flex-1 w-full overflow-hidden">
        {/* Scrollable Container */}
        <div ref={desktopScrollRef} className="flex-1 overflow-y-auto">
          {/* Constrained Width Container - Instagram style max width */}
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Saved Places</h1>
              <p className="text-gray-600">You have {savedPlaces.length} saved place{savedPlaces.length !== 1 ? 's' : ''}</p>
            </div>

            {/* Desktop Grid - Instagram style: 3 columns, minimal gap */}
            <div className="grid grid-cols-3 gap-1">
        {displayedPlaces.map((place, index) => {
          const priceInfo = getPriceInfo(place);
          const isTouched = touchedPlaceId === place.id;
          
          return (
            <div
              key={`desktop-${place.id}`}
              ref={(el) => {
                if (el) {
                  placeRefs.current.set(place.id, el);
                }
                if (index === displayedPlaces.length - 1) {
                  lastItemElementRef(el);
                }
              }}
              className="bg-white overflow-hidden cursor-pointer relative group"
              onClick={() => handlePlaceClick(place)}
              onTouchStart={() => handlePlaceTouch(place)}
            >
              {/* Image - Instagram style square */}
              <div className="aspect-square relative overflow-hidden transition-opacity duration-200 group-hover:opacity-90">
                <img
                  src={place.photo_1_cdn ? getImageSrc(place.photo_1_cdn, 'thumb') : (place.image_url ? getImageSrc(place.image_url, 'thumb') : 'https://via.placeholder.com/300?text=No+Image')}
                  alt={place.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Heart Icon - Always on top */}
                <button
                  onClick={(e) => handleUnsave(place.id, e)}
                  className="heart-icon absolute bottom-2 right-2 p-1.5 rounded-full bg-red-500 text-white shadow-lg hover:bg-red-600 transition-colors duration-200 z-50"
                >
                  <Heart size={16} fill="currentColor" />
                </button>

                {/* "View More" Overlay - Mobile only */}
                {isTouched && (
                  <div 
                    className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-40 md:hidden"
                    onClick={(e) => {
                      if (!(e.target as HTMLElement).closest('.heart-icon')) {
                        setSelectedPlace(place);
                        setTouchedPlaceId(null);
                      }
                    }}
                  >
                    <div className="text-white text-center">
                      <div className="text-xs font-medium">view more</div>
                    </div>
                  </div>
                )}

                {/* Desktop overlay on hover - Instagram style */}
                <div className="hidden md:flex absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 items-center justify-center opacity-0 group-hover:opacity-100">
                  {/* Interaction hint on hover */}
                  <div className="text-white text-center">
                    <div className="text-sm font-semibold">View Details</div>
                  </div>
                </div>

                {/* Type badge - always visible */}
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full capitalize">
                    {place.type}
                  </span>
                </div>

                {/* Add to Trip button - top right */}
              </div>
            </div>
          );
        })}
          </div>

          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}

          {/* Load All button if there are more places to show */}
          {(() => {
            const shouldShow = displayedPlaces.length < savedPlaces.length && savedPlaces.length > 0 && !isLoadingMore && !allPlacesLoaded;
            return shouldShow;
          })() && (
            <div className="flex justify-center py-4">
              <button
                onClick={() => {
                  // Deduplicate savedPlaces by ID to prevent duplicate keys
                  const uniquePlaces = savedPlaces.reduce((acc, place) => {
                    if (!acc.find(p => p.id === place.id)) {
                      acc.push(place);
                    }
                    return acc;
                  }, [] as PlaceItem[]);
                  
                  // Load all remaining places
                  setDisplayedPlaces([...uniquePlaces]); // Create a new array to trigger re-render
                  setCurrentPage(Math.ceil(uniquePlaces.length / itemsPerPage));
                  setAllPlacesLoaded(true);
                }}
                className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Load All ({savedPlaces.length - displayedPlaces.length} more)
              </button>
            </div>
          )}

          {/* End of results indicator */}
          {displayedPlaces.length >= savedPlaces.length && savedPlaces.length > 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>You've reached the end of your saved places</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Mobile Layout - Only show on small screens */}
      <div className="flex md:hidden flex-1 flex-col w-full overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-4 border-b bg-white">
          <h1 className="text-xl font-bold text-gray-800 mb-1">Saved Places</h1>
          <p className="text-sm text-gray-600">You have {savedPlaces.length} saved place{savedPlaces.length !== 1 ? 's' : ''}</p>
        </div>
        
        {/* Scrollable Places Grid - Full Height */}
        <div ref={mobileScrollRef} className="flex-1 overflow-y-auto px-1 py-2">
          <div className="grid grid-cols-3 gap-px">
            {displayedPlaces.map((place, index) => {
              const priceInfo = getPriceInfo(place);
              const isTouched = touchedPlaceId === place.id;
              
              return (
                <div
                  key={`mobile-${place.id}`}
                  ref={(el) => {
                    if (el) {
                      placeRefs.current.set(place.id, el);
                    }
                    if (index === displayedPlaces.length - 1) {
                      lastItemElementRef(el);
                    }
                  }}
                  className="bg-white rounded-none shadow-none overflow-hidden cursor-pointer relative group"
                  onClick={() => handlePlaceClick(place)}
                  onTouchStart={() => handlePlaceTouch(place)}
                >
                  {/* Image - Instagram style portrait (4:5 ratio - taller than wide) */}
                  <div className="aspect-[4/5] relative overflow-hidden">
                    <img
                      src={place.photo_1_cdn ? getImageSrc(place.photo_1_cdn, 'thumb') : (place.image_url ? getImageSrc(place.image_url, 'thumb') : 'https://via.placeholder.com/300?text=No+Image')}
                      alt={place.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Heart Icon - Always on top */}
                    <button
                      onClick={(e) => handleUnsave(place.id, e)}
                      className="heart-icon absolute bottom-2 right-2 p-1 rounded-full bg-red-500 text-white shadow-lg hover:bg-red-600 transition-colors duration-200 z-50"
                    >
                      <Heart size={14} fill="currentColor" />
                    </button>

                    {/* "View More" Overlay - Mobile only */}
                    {isTouched && (
                      <div 
                        className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-40"
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (!target.closest('.heart-icon') && !target.closest('.add-to-trip-icon')) {
                            setSelectedPlace(place);
                            setTouchedPlaceId(null);
                          }
                        }}
                      >
                        <div className="text-white text-center">
                          <div className="text-xs font-medium">view more</div>
                        </div>
                      </div>
                    )}

                    {/* Type Badge */}
                    <div className="absolute top-2 left-2">
                      <span className="bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded-full capitalize">
                        {place.type}
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}

          {/* End of results indicator */}
          {displayedPlaces.length >= savedPlaces.length && savedPlaces.length > 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>You've reached the end of your saved places</p>
            </div>
          )}
        </div>
      </div>

      {/* Place Detail Modal */}
      {selectedPlace && (
        <PlaceDetailPanel
          place={selectedPlace}
          onClose={handleCloseDetail}
          onLoadFullDetails={handleLoadFullDetails}
          isLoadingDetails={isLoadingDetails}
        />
      )}

    </div>
  );
};

export default SavedItems;
